import { Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrAnalyticsApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    analyticsDashboard(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrAnalyticsDashboard>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnalyticsDashboard>>(`${this.baseUrl}/analytics/dashboard`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    analyticsSnapshots(owningCompanyId?: number, category?: string, fromDate?: string, toDate?: string): Observable<APIResponseWrapper<HrModels.HrAnalyticsSnapshot[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnalyticsSnapshot[]>>(`${this.baseUrl}/analytics/snapshots`, {
          params: this.params({ owningCompanyId, category, fromDate, toDate })
        });
      }
    
    aggregateAnalytics(owningCompanyId?: number, snapshotDate?: string): Observable<APIResponseWrapper<HrModels.HrAnalyticsSnapshot[]>> {
        return this.http.post<APIResponseWrapper<HrModels.HrAnalyticsSnapshot[]>>(`${this.baseUrl}/analytics/aggregate`, {}, {
          params: this.params({ owningCompanyId, snapshotDate })
        });
      }
    
    workforceForecasts(owningCompanyId?: number, category?: string): Observable<APIResponseWrapper<HrModels.HrWorkforceForecastScenario[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrWorkforceForecastScenario[]>>(`${this.baseUrl}/analytics/forecasts`, {
          params: this.params({ owningCompanyId, category })
        });
      }
    
    saveWorkforceForecast(request: HrModels.HrWorkforceForecastScenario): Observable<APIResponseWrapper<HrModels.HrWorkforceForecastScenario>> {
        return request.workforceForecastScenarioId
          ? this.http.put<APIResponseWrapper<HrModels.HrWorkforceForecastScenario>>(`${this.baseUrl}/analytics/forecasts/${request.workforceForecastScenarioId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrWorkforceForecastScenario>>(`${this.baseUrl}/analytics/forecasts`, request);
      }
    
    workforceRecommendations(owningCompanyId?: number, status?: string): Observable<APIResponseWrapper<HrModels.HrWorkforceOptimizationRecommendation[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrWorkforceOptimizationRecommendation[]>>(`${this.baseUrl}/analytics/recommendations`, {
          params: this.params({ owningCompanyId, status })
        });
      }
    
    saveWorkforceRecommendation(request: HrModels.HrWorkforceOptimizationRecommendation): Observable<APIResponseWrapper<HrModels.HrWorkforceOptimizationRecommendation>> {
        return request.workforceOptimizationRecommendationId
          ? this.http.put<APIResponseWrapper<HrModels.HrWorkforceOptimizationRecommendation>>(`${this.baseUrl}/analytics/recommendations/${request.workforceOptimizationRecommendationId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrWorkforceOptimizationRecommendation>>(`${this.baseUrl}/analytics/recommendations`, request);
      }
    
    hrAiContext(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrAiContext>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAiContext>>(`${this.baseUrl}/analytics/ai-context`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    analyticsExport(owningCompanyId?: number, format?: string): Observable<APIResponseWrapper<HrModels.HrAnalyticsExport>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnalyticsExport>>(`${this.baseUrl}/analytics/export`, {
          params: this.params({ owningCompanyId, format })
        });
      }

    annualHrReport(scope: HrModels.HrAnnualReportScope, year: number): Observable<APIResponseWrapper<HrModels.HrAnnualManagementReport>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnnualManagementReport>>(`${this.baseUrl}/analytics/annual-report`, {
          params: this.params({ ...scope, year })
        });
      }

    annualHrReportWorkbook(scope: HrModels.HrAnnualReportScope, year: number): Observable<HttpResponse<Blob>> {
        return this.http.get(`${this.baseUrl}/analytics/annual-report/export.xlsx`, {
          params: this.params({ ...scope, year }),
          observe: 'response',
          responseType: 'blob'
        });
      }

    analyticsReportPeriods(scope: HrModels.HrAnnualReportScope, year: number): Observable<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod[]>>(`${this.baseUrl}/analytics/report-periods`, {
          params: this.params({ ...scope, year })
        });
      }

    generateAnalyticsReportPeriod(scope: HrModels.HrAnnualReportScope, year: number, month: number): Observable<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod[]>> {
        return this.http.post<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod[]>>(`${this.baseUrl}/analytics/report-periods/generate`, {
          ...scope,
          year,
          month
        });
      }

    transitionAnalyticsReportPeriod(reportPeriodId: number, action: 'review' | 'lock' | 'reopen', scope: HrModels.HrAnnualReportScope, reason?: string): Observable<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod>> {
        return this.http.post<APIResponseWrapper<HrModels.HrAnalyticsReportPeriod>>(`${this.baseUrl}/analytics/report-periods/${reportPeriodId}/${action}`, {
          ...scope,
          reason
        });
      }

    annualHrMetricDetails(scope: HrModels.HrAnnualReportScope, owningCompanyId: number, year: number, month: number, metricCode: string): Observable<APIResponseWrapper<HrModels.HrAnnualMetricDetails>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnnualMetricDetails>>(`${this.baseUrl}/analytics/metrics/${metricCode}/details`, {
          params: this.params({ ...scope, owningCompanyId, year, month })
        });
      }

    analyticsScorecards(owningCompanyId: number): Observable<APIResponseWrapper<HrModels.HrAnalyticsScorecardRule[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrAnalyticsScorecardRule[]>>(`${this.baseUrl}/analytics/scorecards`, {
          params: this.params({ owningCompanyId })
        });
      }

    saveAnalyticsScorecard(owningCompanyId: number, request: HrModels.HrAnalyticsScorecardRule): Observable<APIResponseWrapper<HrModels.HrAnalyticsScorecardRule>> {
        return this.http.put<APIResponseWrapper<HrModels.HrAnalyticsScorecardRule>>(`${this.baseUrl}/analytics/scorecards`, request, {
          params: this.params({ owningCompanyId })
        });
      }

    overrideAnnualHrStatus(reportPeriodId: number, metricCode: string, scope: HrModels.HrAnnualReportScope, scorecardStatus: string, reason: string): Observable<APIResponseWrapper<HrModels.HrAnnualMetricMonth>> {
        return this.http.put<APIResponseWrapper<HrModels.HrAnnualMetricMonth>>(`${this.baseUrl}/analytics/report-periods/${reportPeriodId}/status-overrides/${metricCode}`, {
          ...scope,
          scorecardStatus,
          reason
        });
      }
  };
}
